import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Backdrop,
  CircularProgress,
  Paper, 
  IconButton
} from '@mui/material';

import DeleteIcon from '@mui/icons-material/Delete';

import { DataGrid ,useGridApiRef } from '@mui/x-data-grid';
import ShowCategoriesDialog from './ShowCategoriesDialog';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';

function getOrderedColumns(dataKeys, codebook, t) {
  const fixedOrder = ["Codini ID", "Respondent ID", "Data", "Sentiment"];
  const categoryCols = dataKeys
    .filter(k => /^Category\s\d+$/.test(k))
    .sort((a, b) => {
      const aNum = parseInt(a.replace("Category ", ""), 10) || 0;
      const bNum = parseInt(b.replace("Category ", ""), 10) || 0;
      return aNum - bNum;
    });
  const rest = dataKeys.filter(k => !fixedOrder.includes(k) && !categoryCols.includes(k));
  const orderedKeys = [
    ...fixedOrder.filter(f => dataKeys.includes(f)),
    ...categoryCols,
    ...rest
  ];

  return orderedKeys.map((key) => {
    let colDef = {
      field: key,
      headerName: key,
      width: 150,
      editable: true,
    };

    if (["Codini ID", "Respondent ID", "Data"].includes(key)) {
      colDef.editable = false;
    }

    if (key === "Sentiment") {
      colDef.type = "singleSelect";
      colDef.valueOptions = ["positive", "negative", "neutral"];
      colDef.headerName = t('dataTab.columns.sentiment') || "Sentiment";
    } else if (/^Category\s\d+$/.test(key)) {
      colDef.type = "singleSelect";
      const catNum = parseInt(key.replace("Category ", ""), 10);
      let options = codebook ? codebook.map(c => c.categoryName) : [];
      // Tüm kategori sütunlarına "Sil" seçeneğini ekle
      options.push(t('dataTab.removeOption') || "Sil");
      colDef.valueOptions = options;
      colDef.headerName = `${t('dataTab.columns.category')} ${catNum}`;
    } else if (key === "Respondent ID") {
      colDef.headerName = t('dataTab.columns.respondentID') || "Respondent ID";
    } else if (key === "Data") {
      colDef.headerName = t('dataTab.columns.data') || "Data";
    }

    return colDef;
  });
}

export default function DashboardDataTab({
  data,
  dashboardCodebook,
  onSaveChanges,
  onDataChanged,
  projectID,
  codeName,
  codeBookLibrary,
  onUpdateCodeBookLibrary  
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const apiRef = useGridApiRef();
  const [columnWidths, setColumnWidths] = useState({});

  const [codebook, setCodebook] = useState(dashboardCodebook || []);
  const [rowData, setRowData] = useState(() => {
    if (!data || data.length === 0) return [];
    return data.map((d, idx) => ({ id: idx, ...d }));
  });
  const [showCatDialog, setShowCatDialog] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('english');

  // Orijinal snapshot'lar
  const [originalDataString, setOriginalDataString] = useState(JSON.stringify(rowData));
  const [originalCodebookString, setOriginalCodebookString] = useState(JSON.stringify(codebook));

  const [rowSelectionModel, setRowSelectionModel] = useState([]);

  // Filtre alanları
  const [dataFilter, setDataFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [sentimentFilter, setSentimentFilter] = useState('');

  // Bulk update alanları
  const [bulkCategoryValue, setBulkCategoryValue] = useState('');
  const [bulkSentimentValue, setBulkSentimentValue] = useState('');

  // "Reanalyze Others" ile ilgili state
  const [showReanalyzeOthersBox, setShowReanalyzeOthersBox] = useState(false);
  const [reanalyzeOthersCategoryList, setReanalyzeOthersCategoryList] = useState([]);
  const [reanalyzeOthersRowData, setReanalyzeOthersRowData] = useState([]);
  const [isReanalyzing, setIsReanalyzing] = useState(false);

  const [isProcessing, setIsProcessing] = useState(false);

  // Değişiklik var mı kontrolü
  const hasUnsavedChanges = useMemo(() => {
    const currentDataString = JSON.stringify(rowData);
    const currentCodebookString = JSON.stringify(codebook);
    return (
      currentDataString !== originalDataString ||
      currentCodebookString !== originalCodebookString
    );
  }, [rowData, codebook, originalDataString, originalCodebookString]);

  // Parent'a "değişiklik var" bilgisini ver
  useEffect(() => {
    if (onDataChanged) {
      onDataChanged(hasUnsavedChanges);
    }
  }, [hasUnsavedChanges, onDataChanged]);

  // Pencere kapanışında uyarı
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // Tek tıkla düzenleme için olay işleyici
  const handleCellClick = useCallback((params) => {
    if (params.isEditable) {
      const cellMode = apiRef.current.getCellMode(params.id, params.field);
      if (cellMode === 'view') {
        apiRef.current.startCellEditMode({ id: params.id, field: params.field });
      }
    }
  }, [apiRef]);

  // Kolon genişliği değiştiğinde güncelleme
  const handleColumnResize = useCallback((params) => {
    setColumnWidths(prev => ({
      ...prev,
      [params.colDef.field]: params.width
    }));
  }, []);

  // Temel kolonlar
  const baseColumns = useMemo(() => {
    if (rowData.length === 0) return [];
    const keys = Object.keys(rowData[0]).filter(k => k !== 'id');
    return getOrderedColumns(keys, codebook, t);
  }, [rowData, codebook, t]);

  // Genişlikleri uygulayan nihai kolonlar
  const columns = useMemo(() => {
    return baseColumns.map(col => ({
      ...col,
      width: columnWidths[col.field] || col.width || 150,
    }));
  }, [baseColumns, columnWidths]);


  // Filtrelenmiş satırlar
  const filteredRows = useMemo(() => {
    return rowData.filter(row => {
      const dataMatch = !dataFilter ||
        (row.Data && String(row.Data).toLowerCase().includes(dataFilter.toLowerCase()));
      const categoryMatch = !categoryFilter ||
        Object.keys(row).some(k => k.startsWith("Category") && row[k] === categoryFilter);
      const sentimentMatch = !sentimentFilter || row.Sentiment === sentimentFilter;

      return dataMatch && categoryMatch && sentimentMatch;
    });
  }, [rowData, dataFilter, categoryFilter, sentimentFilter]);

  // processRowUpdate
  const handleProcessRowUpdate = useCallback((newRow, oldRow) => {
    // Yeni satırı kopyala ve "Sil" seçeneğini null'a çevir
    const updatedRow = { ...newRow };
    Object.keys(updatedRow).forEach((key) => {
      if (key.startsWith("Category ") && updatedRow[key] === (t('dataTab.removeOption') || "Sil")) {
        updatedRow[key] = null;
      }
    });
  
    // Kategori sütunlarını sırayla al
    const catCols = Object.keys(updatedRow)
      .filter(k => /^Category\s\d+$/.test(k))
      .sort((a, b) => {
        const aNum = parseInt(a.replace("Category ", ""), 10) || 0;
        const bNum = parseInt(b.replace("Category ", ""), 10) || 0;
        return aNum - bNum;
      });
  
    // "Category 1" null yapılıyorsa kontrol et
    if (catCols.length > 0 && updatedRow[catCols[0]] === null && oldRow[catCols[0]] !== null) {
      // "Category 2" ve sonrasındaki sütunlarda veri var mı?
      const hasSubsequent = catCols.slice(1).some(col => oldRow[col] !== null);
      if (!hasSubsequent) {
        // Veri yoksa silmeyi engelle ve uyarı göster
        alert(t('dataTab.cannotDeleteFirstCategory') || "İlk kategoriyi silemezsiniz çünkü sonraki kategorilerde veri yok.");
        return oldRow;
      }
    }
  
    // Kategori değerlerini kaydır
    const catValues = catCols.map(col => updatedRow[col]);
    const nonNullCats = catValues.filter(val => val !== null);
    const shiftedRow = { ...updatedRow };
    catCols.forEach((col, index) => {
      shiftedRow[col] = index < nonNullCats.length ? nonNullCats[index] : null;
    });
  
    // Satır verilerini güncelle
    setRowData((prev) =>
      prev.map((row) => (row.id === shiftedRow.id ? shiftedRow : row))
    );
    return shiftedRow;
  }, [t]);

  // Yeni Category sütunu ekle
  const handleAddCategoryColumn = () => {
    const catCols = columns
      .map(c => c.field)
      .filter(k => /^Category\s\d+$/.test(k))
      .sort((a, b) => {
        const aNum = parseInt(a.replace("Category ", ""), 10) || 0;
        const bNum = parseInt(b.replace("Category ", ""), 10) || 0;
        return aNum - bNum;
      });

    let maxCatNum = 0;
    catCols.forEach(col => {
      const num = parseInt(col.replace("Category ", ""), 10);
      if (num > maxCatNum) maxCatNum = num;
    });

    const newCatNum = maxCatNum + 1;
    const newCatName = `Category ${newCatNum}`;

    setRowData(prev =>
      prev.map(r => ({
        ...r,
        [newCatName]: null
      }))
    );
  };

  // Bulk Update Category (Update)
  const handleBulkCategoryUpdate = () => {
    if (!bulkCategoryValue) {
      alert(t('dataTab.selectCategoryUpdate') || "Please select a Category value to bulk update.");
      return;
    }
    if (rowSelectionModel.length === 0) {
      alert(t('dataTab.noRowsSelected') || "No rows selected.");
      return;
    }
    setRowData(prev =>
      prev.map(row => {
        if (rowSelectionModel.includes(row.id)) {
          return { ...row, "Category 1": bulkCategoryValue };
        }
        return row;
      })
    );
    alert(t('dataTab.bulkUpdatedCategory', { category: bulkCategoryValue }) ||
      `Bulk updated selected rows to category: ${bulkCategoryValue}`);
  };

  // Bulk Add Category
  const handleBulkCategoryAdd = () => {
    if (!bulkCategoryValue) {
      alert(t('dataTab.selectCategoryAdd') || "Please select a Category value to bulk add.");
      return;
    }
    if (rowSelectionModel.length === 0) {
      alert(t('dataTab.noRowsSelected') || "No rows selected.");
      return;
    }

    setRowData(prev => {
      const catCols = columns
        .map(c => c.field)
        .filter(k => /^Category\s\d+$/.test(k))
        .sort((a, b) => {
          const aNum = parseInt(a.replace("Category ", ""), 10) || 0;
          const bNum = parseInt(b.replace("Category ", ""), 10) || 0;
          return aNum - bNum;
        });

      let updatedRows = [...prev];
      let addedNewColumn = false;

      updatedRows = updatedRows.map(row => {
        if (!rowSelectionModel.includes(row.id)) return row;

        const alreadyHasThisCat = catCols.some(catCol => row[catCol] === bulkCategoryValue);
        if (alreadyHasThisCat) {
          return row; // pas geç
        }

        let foundSlot = false;
        for (let col of catCols) {
          if (!row[col]) {
            row[col] = bulkCategoryValue;
            foundSlot = true;
            break;
          }
        }
        if (!foundSlot) {
          let maxCatNum = 0;
          catCols.forEach(c => {
            const cNum = parseInt(c.replace("Category ", ""), 10) || 0;
            if (cNum > maxCatNum) maxCatNum = cNum;
          });
          const newCatName = `Category ${maxCatNum + 1}`;
          row[newCatName] = bulkCategoryValue;
          addedNewColumn = true;
        }
        return { ...row };
      });

      if (addedNewColumn) {
        let maxCatNum = 0;
        catCols.forEach(c => {
          const cNum = parseInt(c.replace("Category ", ""), 10) || 0;
          if (cNum > maxCatNum) maxCatNum = cNum;
        });
        const newCatName = `Category ${maxCatNum + 1}`;
        updatedRows = updatedRows.map(r => {
          if (!Object.prototype.hasOwnProperty.call(r, newCatName)) {
            r[newCatName] = null;
          }
          return r;
        });
      }
      return updatedRows;
    });

    alert(t('dataTab.bulkAddedCategory', { category: bulkCategoryValue }) ||
      `Bulk added selected rows to category: ${bulkCategoryValue}`);
  };

  // Bulk Update Sentiment
  const handleBulkSentimentUpdate = () => {
    if (!bulkSentimentValue) {
      alert(t('dataTab.selectSentimentUpdate') || "Please select a Sentiment value to bulk update.");
      return;
    }
    if (rowSelectionModel.length === 0) {
      alert(t('dataTab.noRowsSelected') || "No rows selected.");
      return;
    }
    setRowData(prev =>
      prev.map(row => {
        if (rowSelectionModel.includes(row.id)) {
          return { ...row, Sentiment: bulkSentimentValue };
        }
        return row;
      })
    );
    alert(t('dataTab.bulkUpdatedSentiment', { sentiment: bulkSentimentValue }) ||
      `Bulk updated selected rows' Sentiment to: ${bulkSentimentValue}`);
  };

  // Kategori yönetim diyaloğu
  const handleShowCategories = () => {
    setShowCatDialog(true);
  };
  const handleCloseCategoriesDialog = () => {
    setShowCatDialog(false);
  };
  
  const handleSaveCategories = (updatedList, mergedCategories) => {
    // Rename'leri ayarla
    const oldCategoriesMap = new Map(codebook.map(cat => [cat.categoryCode, cat]));
    
    const renamedCategories = updatedList
      .map(newCat => {
        const oldCat = oldCategoriesMap.get(newCat.categoryCode);
        return oldCat && oldCat.categoryName !== newCat.categoryName 
          ? { oldName: oldCat.categoryName, newName: newCat.categoryName } 
          : null;
      })
      .filter(Boolean);
    
    // Merge bilgisi (mergedCategories örneğin: [{ oldCatName, newCatName }, ...])
    const updatedData = rowData.map(row => {
      const newRow = { ...row };
  
      // Rename uygulama
      renamedCategories.forEach(({ oldName, newName }) => {
        Object.keys(newRow).forEach(key => {
          if (key.startsWith('Category ') && newRow[key] === oldName) {
            newRow[key] = newName;
          }
        });
      });
  
      // Merge uygulama
      mergedCategories.forEach(({ oldCatName, newCatName }) => {
        Object.keys(newRow).forEach(key => {
          if (key.startsWith('Category ') && newRow[key] === oldCatName) {
            newRow[key] = newCatName;
          }
        });
      });
  
      return newRow;
    });
  
    const finalCodebook = updatedList;
    
    setRowData(updatedData);
    setCodebook(finalCodebook);
    setShowCatDialog(false);
  };

  // Kaydet
  const handleSaveClick = async (dataToSave, codebookToSave) => {
    const saveData = dataToSave !== undefined ? dataToSave : rowData;
    const saveCodebook = codebookToSave !== undefined ? codebookToSave : codebook;
  
    if (onSaveChanges) {
      await onSaveChanges(saveData, saveCodebook);
      setOriginalDataString(JSON.stringify(saveData));
      setOriginalCodebookString(JSON.stringify(saveCodebook));
    }
  };
  
  // CSV/JSON/XLSX İndirme İşlemleri
  const convertToCSV = (columns, rows) => {
    const escapeCSVValue = (value) => {
      if (value === null || value === undefined) {
        return '';
      }
      const str = String(value);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };
    const header = columns.map(col => escapeCSVValue(col.headerName)).join(',');
    const dataRows = rows.map(row => 
      columns.map(col => escapeCSVValue(row[col.field])).join(',')
    );
    return [header, ...dataRows].join('\n');
  };

  const convertToJSON = (columns, rows) => {
    return JSON.stringify(rows.map(row => {
      const obj = {};
      columns.forEach(col => {
        obj[col.field] = row[col.field];
      });
      return obj;
    }), null, 2);
  };

  const convertToXLSX = (columns, rows) => {
    const worksheetData = rows.map(row => {
      const obj = {};
      columns.forEach(col => {
        obj[col.headerName] = row[col.field];
      });
      return obj;
    });
    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    return XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  };

  const downloadFile = (content, fileName, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadCSV = () => {
    const csvContent = convertToCSV(columns, filteredRows);
    downloadFile(csvContent, 'data.csv', 'text/csv;charset=utf-8;');
  };

  const downloadJSON = () => {
    const jsonContent = convertToJSON(columns, filteredRows);
    downloadFile(jsonContent, 'data.json', 'application/json;charset=utf-8;');
  };

  const downloadXLSX = () => {
    const xlsxContent = convertToXLSX(columns, filteredRows);
    downloadFile(xlsxContent, 'data.xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
  };

  // Reanalyze Others
  const reanalyzeOthers = async () => {
    const confirmation = window.confirm(
      t('dataTab.reanalyzeConfirm') || "Save changes? Any modifications made to the data will be processed after saving. If you do not save, the data will revert to its last saved state."
    );
    if (confirmation) {
      await handleSaveClick();
    }
    setIsReanalyzing(true);
    const formData = new FormData();
    formData.append('projectID', projectID || '');
    formData.append('codeName', codeName || '');

    try {
      const response = await fetch("https://codini-project-others-7kpxqstxwq-ey.a.run.app", {
        method: "POST",
        body: formData
      });
      const data = await response.json();

      const otherCodebook = data.updated_category_list;
      const otherRowData = data.filtered_result_data;
      const newCatList = otherCodebook.map(cat => ({
        categoryName: cat.category_name,
        categoryCode: cat.category_code,
        readOnly: cat.category_code ? true : false
      }));

      setReanalyzeOthersCategoryList(newCatList);
      setReanalyzeOthersRowData(otherRowData);
      setShowReanalyzeOthersBox(true);

    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsReanalyzing(false);
    }
  };

  const handleAddCategoryOther = () => {
    setReanalyzeOthersCategoryList(prev => [
      ...prev,
      { categoryName: '', categoryCode: '', readOnly: false }
    ]);
  };

  const handleSaveReanalyzeOthersCategories = () => {
    setIsProcessing(true);
    let listItems = reanalyzeOthersCategoryList;
    let maxCode = 0;
  
    listItems.forEach(item => {
      const code = parseInt(item.categoryCode, 10);
      if (!isNaN(code) && code !== 998) {
        maxCode = Math.max(maxCode, code);
      }
    });
  
    const updatedList = listItems.map(item => {
      if (!item.categoryCode) {
        maxCode += 1;
        return {
          ...item,
          categoryCode: maxCode.toString()
        };
      }
      return item;
    });
  
    if (updatedList.length > 200) {
      alert(t('codebook.exceedCategories') || 'The codebook should not exceed 200 categories.');
      return;
    }
  
    const formData = new FormData();
    formData.append("projectID", projectID || '');
    formData.append("updatedCategoryList", JSON.stringify(updatedList.map(u => ({
      categoryName: u.categoryName,
      categoryCode: u.categoryCode
    }))));
    formData.append("codeName", codeName || '');
    formData.append('userEmail', currentUser.email);
    formData.append("otherRowData", JSON.stringify(reanalyzeOthersRowData));
  
    const fetchPromise = fetch("https://codini-project-other-reanalyze-7kpxqstxwq-ey.a.run.app", {
      method: "POST",
      body: formData
    })
      .then(() => {
        // İşlem tamamlandığında
      })
      .catch((error) => {
        console.error('Error sending project ID:', error);
      });
  
    const timeoutPromise = new Promise(resolve =>
      setTimeout(() => resolve('timeout'), 7000)
    );
    Promise.race([fetchPromise, timeoutPromise])
      .then(() => {
        setShowReanalyzeOthersBox(false);
        navigate("/ProjectsPage");
      })
      .catch(error => {
        console.error(t('codebook.saveError'), error);
        setShowReanalyzeOthersBox(false);
        navigate("/ProjectsPage");
      })
      .finally(() => {
        setIsProcessing(false); // End processing
      });

  };

  const handleCancelReanalyzeOthers = () => {
    setShowReanalyzeOthersBox(false);
  };

  // Çeviri fonksiyonu
  const handleTranslate = async () => {
    setIsTranslating(true);
    const formData = new FormData();
    formData.append('projectID', projectID || '');
    formData.append('codeName', codeName || '');
    formData.append('language', selectedLanguage);
  
    try {
      const response = await fetch("https://codini-project-translation-795950092325.europe-west3.run.app", {
        method: "POST",
        body: formData
      });
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      const translationData = data.updated_category_list;
  
      // Çeviri haritasını oluştur
      const translationMap = {};
      translationData.forEach(item => {
        translationMap[item.question_code] = item.translation;
      });
  
      // RowData'yı çeviriyle güncelle
      const updatedRowData = rowData.map(row => ({
        ...row,
        Translation: translationMap[row['Respondent ID']] || ''
      }));
  
      setRowData(updatedRowData);
  
      // Çeviriyi kaydetmek isteyip istemediği sorulsun
      const shouldSave = window.confirm(t('dataTab.saveTranslationConfirm') || 'Do you want to save the translation?');
      if (shouldSave) {
        try {
          setIsTranslating(false);
          await handleSaveClick(updatedRowData, codebook);
        } catch (saveError) {
          console.error('Error saving translation:', saveError);
          alert(t('dataTab.saveError') || 'Error saving translation.');
        }
      }
    } catch (error) {
      console.error('Error during translation:', error);
      alert(t('dataTab.translationError') || 'An error occurred during translation.');
    } finally {
      setIsTranslating(false);
    }
  };

  if (!rowData || rowData.length === 0) {
    return <Typography>{t('dataTab.noData') || "No data available"}</Typography>;
  }

  return (
    <Box>
      {isReanalyzing && (
        <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isReanalyzing}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      {isTranslating && (
        <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isTranslating}>
          <Box display="flex" flexDirection="column" alignItems="center">
            <CircularProgress color="inherit" />
            <Typography variant="h6" sx={{ mt: 2, color: '#fff' }}>
              {t('dataTab.translationWait') || "Please wait for translation..."}
            </Typography>
          </Box>
        </Backdrop>
      )}

      {/* Filtreler & Toplu Update & Setup */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        {/* Filters */}
        <Box sx={{ flex: 1, minWidth: 200, p: 2, bgcolor: 'green.100', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            {t('dataTab.filters') || "Filters"}
          </Typography>
          <TextField
            fullWidth
            placeholder={t('dataTab.searchInData') || "Search in Data"}
            variant="outlined"
            size="small"
            value={dataFilter}
            onChange={(e) => setDataFilter(e.target.value)}
          />
          <FormControl fullWidth sx={{ mt: 1 }} size="small">
            <InputLabel>{t('dataTab.category') || "Category"}</InputLabel>
            <Select
              value={categoryFilter}
              label={t('dataTab.category') || "Category"}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <MenuItem value="">
                <em>{t('dataTab.allCategories') || "All Categories"}</em>
              </MenuItem>
              {codebook.map((catObj, i) => (
                <MenuItem key={i} value={catObj.categoryName}>
                  {catObj.categoryName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mt: 1 }} size="small">
            <InputLabel>{t('dataTab.sentiment') || "Sentiment"}</InputLabel>
            <Select
              value={sentimentFilter}
              label={t('dataTab.sentiment') || "Sentiment"}
              onChange={(e) => setSentimentFilter(e.target.value)}
            >
              <MenuItem value="">
                <em>{t('dataTab.allSentiments') || "All Sentiments"}</em>
              </MenuItem>
              <MenuItem value="positive">positive</MenuItem>
              <MenuItem value="negative">negative</MenuItem>
              <MenuItem value="neutral">neutral</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Bulk Update */}
        <Box sx={{ flex: 1, minWidth: 200, p: 2, bgcolor: 'indigo.100', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            {t('dataTab.bulkUpdateTitle') || "Bulk Update Selected Rows"}
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>{t('dataTab.categoryValue') || "Category Value"}</InputLabel>
            <Select
              value={bulkCategoryValue}
              label={t('dataTab.categoryValue') || "Category Value"}
              onChange={(e) => setBulkCategoryValue(e.target.value)}
            >
              <MenuItem value="">
                <em>{t('dataTab.none') || "None"}</em>
              </MenuItem>
              {codebook.map((catObj, idx) => (
                <MenuItem key={idx} value={catObj.categoryName}>
                  {catObj.categoryName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            size="small"
            onClick={handleBulkCategoryUpdate}
            sx={{ mb: 2, mr: 1 }}
          >
            {t('dataTab.updateCategory') || "Update Category"}
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleBulkCategoryAdd}
            sx={{ mb: 2 }}
          >
            {t('dataTab.addCategory') || "Add Category"}
          </Button>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>{t('dataTab.sentimentValue') || "Sentiment Value"}</InputLabel>
            <Select
              value={bulkSentimentValue}
              label={t('dataTab.sentimentValue') || "Sentiment Value"}
              onChange={(e) => setBulkSentimentValue(e.target.value)}
            >
              <MenuItem value="">
                <em>{t('dataTab.none') || "None"}</em>
              </MenuItem>
              <MenuItem value="positive">positive</MenuItem>
              <MenuItem value="negative">negative</MenuItem>
              <MenuItem value="neutral">neutral</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" size="small" onClick={handleBulkSentimentUpdate}>
            {t('dataTab.updateSentiment') || "Update Sentiment"}
          </Button>
        </Box>

        {/* Setup Section */}
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 200, p: 2, bgcolor: 'red.100', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            {t('dataTab.setup') || "Setup"}
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={handleShowCategories}
            sx={{ mb: 1 }}
          >
            {t('dataTab.manageCodebook') || "Manage Codebook"}
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={reanalyzeOthers}
            sx={{ mb: 1 }}
          >
            {t('dataTab.reanalyzeOthers') || "Reanalyze Others"}
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => handleSaveClick()} 
          >
            {t('dataTab.saveChanges') || "Save Changes"}
          </Button>
        </Box>
      </Box>

      {/* "Add New Category Column" */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
        <Button variant="contained" size="small" onClick={handleAddCategoryColumn}>
          {t('dataTab.addNewCategoryColumn') || "Add New Category Column"}
        </Button>

        {/* Dil Seçimi ve Çevir Butonu */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>{t('dataTab.language') || "Language"}</InputLabel>
            <Select
              value={selectedLanguage}
              label={t('dataTab.language') || "Language"}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              variant="outlined"
            >
              <MenuItem value="english">English</MenuItem>
              <MenuItem value="german">German</MenuItem>
              <MenuItem value="french">French</MenuItem>
              <MenuItem value="italian">Italian</MenuItem>
              <MenuItem value="spanish">Spanish</MenuItem>
              <MenuItem value="turkish">Turkish</MenuItem>
            </Select>
          </FormControl>

          <Button variant="contained" size="small" onClick={handleTranslate}>
            {t('dataTab.translate') || "Translate"}
          </Button>
        </Box>
      </Box>

      {/* DataGrid */}
      <Box sx={{ height: 400, width: '100%' }}>
        <DataGrid
          apiRef={apiRef} // API referansı eklendi
          rows={filteredRows}
          columns={columns}
          checkboxSelection
          disableSelectionOnClick
          experimentalFeatures={{ newEditingApi: true }}
          editMode="cell"
          pageSize={5}
          rowsPerPageOptions={[5, 10, 20]}
          processRowUpdate={handleProcessRowUpdate}
          onRowSelectionModelChange={(newSelection) => setRowSelectionModel(newSelection)}
          onCellClick={handleCellClick} // Tek tıkla düzenleme eklendi
          onColumnResize={handleColumnResize} // Kolon genişliği koruma eklendi
        />
      </Box>

      {/* İndirme Butonları */}
      <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
        <Button variant="contained" size="small" onClick={downloadCSV}>
          {t('dataTab.downloadCSV') || "⬇️ CSV"}
        </Button>
        <Button variant="contained" size="small" onClick={downloadJSON}>
          {t('dataTab.downloadJSON') || "⬇️ JSON"}
        </Button>
        <Button variant="contained" size="small" onClick={downloadXLSX}>
          {t('dataTab.downloadXLSX') || "⬇️ XLSX"}
        </Button>
      </Box>

      {/* Kategori Yönetim Diyaloğu */}
      <ShowCategoriesDialog
        open={showCatDialog}
        onClose={handleCloseCategoriesDialog}
        categories={codebook}
        onSave={handleSaveCategories}
        codeBookLibrary={codeBookLibrary}
        onSaveToLibrary={(newCodebook) => {
          return new Promise((resolve, reject) => {
            const updatedLibrary = {
              codebooks: [...codeBookLibrary.codebooks, newCodebook]
            };

            const formData = new FormData();
            formData.append('useremail', currentUser.email);
            formData.append('codeBookLibrary', JSON.stringify(updatedLibrary));

            fetch('https://codini-porejct-codebookupdate-gt-795950092325.europe-west3.run.app', {
              method: 'POST',
              body: formData
            })
              .then(response => {
                if (!response.ok) {
                  throw new Error('Failed to save codebook');
                }
                return response.json();
              })
              .then(data => {
                const updatedFromServer = data.codeBookLibrary?.codebooks || updatedLibrary.codebooks;
                if (onUpdateCodeBookLibrary) {
                  onUpdateCodeBookLibrary({ codebooks: updatedFromServer });
                }
                setShowCatDialog(false);
                resolve();
              })
              .catch(error => {
                console.error('Error saving codebook:', error);
                reject(error);
              });
          });
        }}
      />

      {/* "Reanalyze Others" Kutusu */}
      {showReanalyzeOthersBox && (
        <Box
          sx={{
            position: 'fixed',
            zIndex: 9999,
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Box
            sx={{
              width: '60%',
              maxHeight: '80vh',
              overflowY: 'auto',
              backgroundColor: 'white',
              borderRadius: 2,
              p: 3
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 2, mb: 2 }}>       
              <Button variant="outlined" onClick={handleCancelReanalyzeOthers}>
                {t('cancel') || "Cancel"}
              </Button>
            </Box>
            <Paper elevation={1} sx={{ p: 1, mb: 2, background:'#fed9b7' }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {t('dataTab.reanalyzeOthersTitle') || "Reanalyze Others"}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {t('dataTab.reanalyzeOthersDescription') ||
                  "Including categories for values such as Unknown, No response, or Junk helps prevent these from being assigned to the Other category. Below is the current Codebook, along with suggestions by AI."}
              </Typography>
            </Paper>
           
            {reanalyzeOthersCategoryList.map((cat, idx) => (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TextField
                  label={t('dataTab.code') || "Code"}
                  size="small"
                  type="number"
                  disabled={cat.readOnly}
                  value={cat.categoryCode}
                  sx={{ minWidth: 60, mr: 0.5 }}
                  onChange={(e) => {
                    const val = e.target.value;
                    setReanalyzeOthersCategoryList(prev => {
                      const newList = [...prev];
                      newList[idx] = { ...newList[idx], categoryCode: val };
                      return newList;
                    });
                  }}
                />
                <TextField
                  label={t('dataTab.name') || "Name"}
                  size="small"
                  disabled={cat.readOnly}
                  value={cat.categoryName}
                  sx={{ flexGrow: 1, mr: 0.5, background:"#fff" }}
                  onChange={(e) => {
                    const val = e.target.value;
                    setReanalyzeOthersCategoryList(prev => {
                      const newList = [...prev];
                      newList[idx] = { ...newList[idx], categoryName: val };
                      return newList;
                    });
                  }}
                />
                {!cat.readOnly && (
                  <IconButton
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setReanalyzeOthersCategoryList(prev => {
                        const newList = [...prev];
                        newList.splice(idx, 1);
                        return newList;
                      });
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>                                               
                )}
              </Box>
            ))}
            <Box sx={{ mb: 2, textAlign: 'center' }}>
              <Button
                variant="contained"
                size="small"
                onClick={handleAddCategoryOther}
                sx={{ mb: 2 }}
              >
                {t('dataTab.addCategory') || "Add Category"}
              </Button>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2, mt: 3 }}>
              <Button
                variant="contained"
                size="small"
                color="success"
                onClick={handleSaveReanalyzeOthersCategories}
              >
                {isProcessing ? (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CircularProgress size={24} sx={{ color: 'white', mr: 1 }} />
                    {t('dataTab.processing') || "Processing..."}
                  </Box>
                ) : (
                  t('dataTab.startAnalysis') || "Start Analysis"
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}